import colors from 'shared/colors';
import useChartValueFormatter from '@/composables/grid-page/chart/chart-value-formatter';
import { chartHasMultipleSeries, computeLabelStep, dataLabelStyle, escapeHtml, formatNoData, formatTooltipCard, formatTooltipRow, isDateGrouping, nudgeFirstLabel, syncReferenceLinesToAxis } from '@/composables/grid-page/chart/utils';
import { computed } from 'vue';
import { gridPageChartType } from 'shared/constants.json';
import { useI18n } from 'vue-i18n';

export default function useBarChart(result, chart, onPointClick) {
  const { t } = useI18n();
  const { formatValue } = useChartValueFormatter(chart);
  const hasMultipleSeries = computed(() => chartHasMultipleSeries(chart.value));
  const isStacked = computed(() => hasMultipleSeries.value && (chart.value.stacked ?? true));

  const series = computed(() => {
    if (![gridPageChartType.bar, gridPageChartType.column].includes(chart.value.chartType)) {
      return [];
    }

    if (result.value === null) {
      return [];
    }

    return result.value.series;
  });

  const categories = computed(() => {
    if (result.value === null) {
      return [];
    }

    return result.value.categories;
  });

  const isBarType = computed(() => chart.value.chartType === gridPageChartType.bar);

  const handleCategoryClick = function handleCategoryClick(e) {
    const click = onPointClick?.value;
    if (!click) {
      return;
    }
    const xAxis = this.xAxis[0];
    const index = Math.round(xAxis.toValue(isBarType.value ? e.chartY - this.plotTop : e.chartX - this.plotLeft, true));
    if (index >= 0 && index < categories.value.length) {
      click(index);
    }
  };

  const categoryAxisConfig = computed(() => {
    if (isDateGrouping(chart.value.groupBy?.groupBy)) {
      return {
        tickInterval: computeLabelStep(categories.value.length),
        labels: { autoRotation: false },
      };
    }
    if (isBarType.value) {
      return { labels: { style: { textOverflow: 'ellipsis', width: '200px' } } };
    }
    return { labels: { autoRotation: [-45], style: { textOverflow: 'ellipsis', width: '80px' } } };
  });

  const chartOptions = computed(() => ({
    chart: {
      animation: false,
      type: chart.value.chartType,
      plotBackgroundColor: 'rgba(0,0,0,0)',
      events: {
        click: handleCategoryClick,
        render() {
          nudgeFirstLabel(this);
          syncReferenceLinesToAxis(this, chart.value.referenceLines);
        },
      },
    },
    plotOptions: {
      [chart.value.chartType]: {
        colorByPoint: !hasMultipleSeries.value,
        maxPointWidth: 16,
        borderRadius: 3,
        pointPadding: 0,
        groupPadding: 0.2,
        borderWidth: 0,
        borderColor: 'transparent',
        states: {
          hover: { brightness: 0, borderColor: 'transparent', borderWidth: 0 },
          inactive: { enabled: false },
        },
        cursor: onPointClick?.value ? 'pointer' : 'default',
        dataLabels: {
          enabled: !hasMultipleSeries.value,
          inside: false,
          overflow: 'allow',
          crop: false,
          style: dataLabelStyle,
          formatter() {
            return this.y === 0 ? null : formatValue(this.y);
          },
          ...(isBarType.value
            ? { align: 'left', x: 2, verticalAlign: 'middle', y: -1 }
            : { verticalAlign: 'bottom', y: -2 }),
        },
        stacking: isStacked.value ? 'normal' : undefined,
        point: {
          events: {
            click() {
              const click = onPointClick?.value;
              if (click) {
                click(this.index);
              }
            },
          },
        },
      },
    },
    legend: {
      enabled: hasMultipleSeries.value,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      y: 10,
      events: {
        itemClick(e) {
          const chart = this.chart;
          const visibleCount = chart.series.filter((s) => s.visible).length;

          if (visibleCount === 1 && e.legendItem.visible) {
            return false;
          }
          return true;
        },
      },
    },
    xAxis: {
      categories: categories.value,
      showEmpty: series.value.length > 0,
      lineWidth: 1,
      ...categoryAxisConfig.value.tickInterval && { tickInterval: categoryAxisConfig.value.tickInterval },
      labels: {
        enabled: true,
        ...categoryAxisConfig.value.labels,
      },
      crosshair: { color: colors.grey.lighten6, zIndex: 0 },
    },
    yAxis: {
      title: { text: null },
      labels: { enabled: true },
      gridLineWidth: 1,
      reversedStacks: false,
      stackLabels: {
        enabled: isStacked.value,
        style: dataLabelStyle,
        ...(isBarType.value
          ? { x: 2 }
          : { y: -2 }),
      },
    },
    series: series.value,
    tooltip: {
      shared: true,
      formatter() {
        const points = this.points;
        const noData = t('highcharts.noDataShort');
        if (hasMultipleSeries.value && points.length > 1) {
          const header = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${escapeHtml(points[0].key)}</div>`;
          const rows = points.filter((p) => p.y !== 0).map((p) => formatTooltipRow(p.color, p.series.name, formatValue(p.y))).join('');
          return formatTooltipCard(header + (rows || formatNoData(noData)));
        }
        const p = points[0];
        if (p.y === 0) {
          const header = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${escapeHtml(p.key || p.point.name)}</div>`;
          return formatTooltipCard(header + formatNoData(noData));
        }
        return formatTooltipCard(formatTooltipRow(p.color, p.key || p.point.name, formatValue(p.y)));
      },
      positioner(labelWidth, labelHeight, point) {
        const { plotLeft, plotTop, plotWidth, plotHeight } = this.chart;
        const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

        if (this.chart.inverted) {
          let x = point.plotX + plotLeft + 16;
          if (x + labelWidth > plotLeft + plotWidth) {
            x = point.plotX + plotLeft - labelWidth - 16;
          }
          return {
            x: clamp(x, plotLeft, plotLeft + plotWidth - labelWidth),
            y: clamp(point.plotY + plotTop - labelHeight / 2, plotTop, plotTop + plotHeight - labelHeight),
          };
        }
        return {
          x: clamp(point.plotX + plotLeft - labelWidth / 2, plotLeft, plotLeft + plotWidth - labelWidth),
          y: Math.max(point.plotY + plotTop - labelHeight - 12, 0),
        };
      },
    },
  }));

  return { chartOptions };
}
